<template>
  <div class="tw-mb-2 tw-flex tw-flex-col tw-gap-2">
    <c-label v-if="label" :label />

    <div class="tw-flex tw-flex-wrap tw-gap-4">
      <div
        v-for="(file, key) in shownImages"
        v-if="!hideList"
        :key="key"
        class="attachment attachment--file tw-relative tw-flex tw-p-2"
      >
        <div class="tw-absolute -tw-right-2 -tw-top-4 tw-flex tw-gap-x-1">
          <button
            v-if="preview"
            :id="`file-${key}`"
            class="file tw-flex tw-aspect-square tw-w-[25px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-100 tw-transition hover:tw-bg-primary-200"
            :data-src="file.path"
            :data-download="file.name"
          >
            <q-icon name="fas fa-magnifying-glass" color="primary" />
          </button>

          <div
            v-if="!readonly || removable"
            class="tw-flex tw-aspect-square tw-w-[25px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100 tw-transition hover:tw-bg-red-200"
            @click="handleRemoveAttachment(file)"
          >
            <q-icon name="fas fa-trash" color="negative" />
          </div>
        </div>

        <AttachmentPreviewComponent :attachment="file" />
      </div>

      <label
        v-if="showAddAttachmentButton"
        class="attachment attachment--upload"
        :for="id"
      >
        <q-icon size="small" name="fa-duotone fa-paperclip" />

        <span>Bijlage toevoegen</span>
      </label>
    </div>

    <p v-if="!readonly" class="tw-m-0 tw-text-xs tw-text-gray-700">
      Toegestane bestandstypen:
      {{ getAllowedFileTypeString }}
    </p>

    <p v-if="fileSizeLimit" class="tw-m-0 tw-text-xs tw-text-gray-700">
      Max. {{ fileSizeLimit }}MB per bestand
    </p>

    <input
      v-if="!readonly"
      :id="id"
      ref="file"
      class="tw-hidden"
      type="file"
      :name="id"
      :accept="getAllowedFileTypeString"
      :multiple
      @change="handleInput"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import {
  allowedFileTypes as generateAllowedFileTypes,
  getExtensionFromMimeType,
  invalidFileMessage,
} from "composables/useHelpers";
import AttachmentPreviewComponent from "components/global/AttachmentPreviewComponent.vue";
import { useQuasar } from "quasar";
import CDialog from "shared/components/wrappers/dialog/CDialog.vue";
import { useId } from "quasar";
import lightGallery from "lightgallery";
import "lightgallery/css/lightgallery.css";

// Model
const model = defineModel({
  type: Array,
  required: true,
  default: () => [],
});

// Random ID
const id = useId();

// Emits
const emit = defineEmits(["delete", "deleteSuccess", "deleteError"]);

// Quasar
const $q = useQuasar();

// Props
const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  allowedFileTypes: {
    type: Array,
    default: () => [],
    required: false,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  fileSizeLimit: {
    type: Number,
    required: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  preview: {
    type: Boolean,
    default: false,
  },
  removable: {
    type: Boolean,
    default: false,
  },
  hideList: {
    type: Boolean,
    default: false,
  },
});

// Refs
const file = ref(null);

// Computeds
const isArray = computed(() => {
  return Array.isArray(model.value);
});

const shownImages = computed(() => {
  if (props.multiple && isArray.value) {
    return model.value;
  } else {
    // Return array with 1 value to loop over in the template.
    // this way we don't need to do conditional rendering in the template
    return model.value ? [model.value] : [];
  }
});

const getAllowedFileTypes = computed(() => {
  const { allowedFileTypes = [] } = props || {};

  if (!Array.isArray(allowedFileTypes) || allowedFileTypes.length === 0) {
    return generateAllowedFileTypes();
  }

  return allowedFileTypes;
});

const getAllowedFileTypeString = computed(() =>
  getAllowedFileTypes.value
    .map((fileType) => `.${getExtensionFromMimeType(fileType)}`)
    .join(", "),
);

// Methods
const handleInput = () => {
  for (const value of Object.values(file.value.files)) {
    const filetype = value.type;

    if (!getAllowedFileTypes.value.includes(filetype)) {
      alert(invalidFileMessage);

      continue;
    }

    // Enforce file size limit (in MB) if set
    if (typeof props.fileSizeLimit === "number" && props.fileSizeLimit > 0) {
      const maxBytes = props.fileSizeLimit * 1024 * 1024;
      if (value.size > maxBytes) {
        $q.notify({
          type: "negative",
          message: `Bestand "${value.name}" is te groot (${(value.size / 1024 / 1024).toFixed(1)}MB). Maximaal ${props.fileSizeLimit}MB toegestaan.`,
        });
        // Skip this file but continue processing others (if any)
        continue;
      }
    }

    if (props.multiple) {
      model.value.push(value);
    } else {
      model.value = value;
    }
  }

  // Reset the file input so changes will be reflected
  // Was an issue where one removed the file and added the same file again
  file.value.value = "";
};

const removeAttachment = (file) => {
  if (props.multiple && isArray.value) {
    model.value = model.value.filter((value) => value !== file);
  } else {
    model.value = null;
  }
};

const handleRemoveAttachment = (file) => {
  $q.dialog({
    component: CDialog,
    componentProps: {
      title: "Bijlage verwijderen",
      message: "Weet je zeker dat je deze bijlage wilt verwijderen?",
      cancel: true,
      primaryLabel: "Bijlage verwijderen",
    },
  }).onOk(() => {
    // Emit delete event with callbacks for success/error handling
    emit("delete", file, {
      onSuccess: () => {
        // Remove locally only after successful server deletion
        removeAttachment(file);
        $q.notify({
          type: "positive",
          message: "Bijlage succesvol verwijderd",
        });
      },
      onError: (error) => {
        console.error("Failed to delete attachment:", error);
        $q.notify({
          type: "negative",
          message:
            error?.message ||
            "Er is iets fout gegaan bij het verwijderen van de bijlage",
        });
      },
    });
  });
};

onMounted(() => {
  if (!props.preview) {
    return;
  }

  const files = document.querySelectorAll(".file");

  files.forEach((file) => {
    lightGallery(file, {
      licenseKey: import.meta.env?.VITE_LIGHTGALLERY_LICENSE_KEY,
      selector: "this",
      enableDrag: false,
      counter: false,
      addClass: "light-gallery",
    });
  });
});

const showAddAttachmentButton = computed(() => {
  return (
    !props.readonly &&
    ((model.value.length === 0 && !props.multiple) || props.multiple)
  );
});
</script>

<style lang="scss">
.attachment {
  @apply tw-flex tw-aspect-square tw-w-[100px] tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-text-balance tw-rounded tw-p-2 tw-text-center tw-text-xs tw-transition;

  &--upload {
    @apply tw-cursor-pointer tw-border tw-border-gray-300 tw-bg-primary-50 hover:tw-bg-primary-100;
  }

  &--file {
    @apply tw-select-none tw-border tw-border-primary-100;
  }
}
</style>
